import Axios from '../../utils/axios';
import { getBackendErrorMessage } from '../../utils/errorHandler';

// The /client-user router rejects a request without X-Channel ("header
// x-channel is required"), so every endpoint under it carries these — same as
// deliveries/invoices/packages/shipments. X-Api-Mode is not read by the
// pitstops route today, but it is by other /client-user routes, so it stays
// for consistency with the rest of the FE. `/v1/pitstop` below is mounted
// outside that router and needs neither.
const CHANNEL_HEADERS_LIVE = {
  'X-Channel': 'LINK',
  'X-Api-Mode': 'live',
} as const;

export interface Pitstop {
  id: number;
  ID?: number;
  name: string;
  address?: string;
  /** `HUB` = Dash-operated x-dock; `PROVIDER_WAREHOUSE` = the client's own pitstop. */
  pitstopType?: string;
  city: {
    id: number;
    name: string;
    code: string;
    uuid: string;
    province_id: number;
    is_active: boolean;
    created_at: string;
    timezone: string;
    google_place_id: string;
    wilayahid_code: string;
  };
  cityID?: number;
  cityName?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface PitstopListResponse {
  data: Pitstop[];
  pagination: {
    total: number;
    page: number;
    size: number;
    lastPage: number;
  };
}

// Dash-operated locations (hubs). Unscoped by design — hubs are shared
// infrastructure, not client property. For the client's OWN pickup points use
// `getMyPitstops` below: this endpoint's `providerID` param is caller-supplied
// and filters nothing when omitted, so it is not a tenant boundary.
export const getListPitstop = async (params: {
  page?: string;
  size?: string;
  search?: string;
  type?: string;
  hasStaff?: boolean;
}): Promise<PitstopListResponse> => {
  try {
    const response = await Axios.get('/v1/pitstop', { params });
    return response.data;
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

/** One of the signed-in client's own pickup points. */
export interface ClientPitstop {
  id: number;
  uuid: string | null;
  name: string;
  code: string;
  address: string;
  /** `PROVIDER_WAREHOUSE` for a client-owned pitstop. */
  pitstopType: string;
}

export interface ClientPitstopListResponse {
  data: ClientPitstop[];
  pagination: {
    total: number;
    page: number;
    size: number;
    lastPage: number;
  } | null;
}

/**
 * The client's own pitstops. The tenant scope comes from the bearer token
 * server-side, so there is no client id to pass and nothing to re-filter here
 * — unlike `/v1/pitstop`, a caller cannot widen this to another tenant.
 *
 * Visibility follows the same rule as outlets: a super-admin sees every
 * pitstop of the client, everyone else sees only the ones they're assigned to.
 */
export const getMyPitstops = async (params: {
  page?: string;
  size?: string;
  search?: string;
  type?: string;
}): Promise<ClientPitstopListResponse> => {
  try {
    const response = await Axios.get('/client-user/v1/clients/me/pitstops', {
      headers: CHANNEL_HEADERS_LIVE,
      params,
    });
    return response.data;
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};
